/**
 * AiSplitPdfDialog — Feature 1.
 * Upload a single chapter PDF, choose number of lessons, kick the job, then
 * close immediately and hand off to the global AI Jobs tray.
 */
import React, { useRef, useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollText, Upload, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAiJobs } from "@/lib/ai-jobs";


export default function AiSplitPdfDialog({
    open, onOpenChange, chapter, onQueued,
}) {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [count, setCount] = useState(8);
    const [busy, setBusy] = useState(false);
    const { trackJob } = useAiJobs();

    const reset = () => { setFile(null); setCount(8); setBusy(false); };
    const close = () => { reset(); onOpenChange(false); };

    const submit = async () => {
        if (!chapter?.id) return;
        if (!file) { toast.error("Pick a PDF first."); return; }
        if (count < 2 || count > 25) {
            toast.error("Pick between 2 and 25 lessons."); return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("target_count", String(count));
            const { data } = await api.post(
                `/chapters/${chapter.id}/lessons/ai-split-pdf`,
                fd, { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success(`Splitting "${file.name}" into ${count} lessons — track progress in the AI jobs tray.`);
            trackJob({
                kind: "split",
                jobId: data.job_id,
                chapterId: chapter.id,
                chapterTitle: chapter.title,
                total: count,
                onDone: () => onQueued?.(),
            });
            close();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start the split.");
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => (!o ? close() : null)}>
            <DialogContent className="max-w-md" data-testid="ai-split-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl flex items-center gap-2">
                        <ScrollText className="h-5 w-5 text-primary" />
                        Split chapter PDF into lessons
                    </DialogTitle>
                    <DialogDescription>
                        Uploads to the AI worker. The dialog will close once
                        the job is queued — track progress in the AI jobs tray
                        in the bottom-right corner.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 pt-1">
                    <div>
                        <Label className="text-xs font-semibold">Chapter</Label>
                        <div className="text-sm text-muted-foreground truncate">
                            {chapter?.title || "—"}
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Source PDF</Label>
                        <div className="flex items-center gap-2 mt-1">
                            <Input
                                type="file"
                                accept="application/pdf"
                                ref={fileRef}
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="ai-split-file-input"
                            />
                        </div>
                        {file ? (
                            <div className="text-[11px] text-muted-foreground mt-1">
                                {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                        ) : null}
                    </div>

                    <div>
                        <Label className="text-xs font-semibold">Number of lessons</Label>
                        <div className="flex items-center gap-3 mt-1">
                            <input
                                type="range"
                                min={2}
                                max={25}
                                value={count}
                                onChange={(e) => setCount(parseInt(e.target.value, 10))}
                                className="flex-1 accent-primary"
                                data-testid="ai-split-count-slider"
                            />
                            <Input
                                type="number"
                                min={2}
                                max={25}
                                value={count}
                                onChange={(e) => setCount(parseInt(e.target.value, 10) || 8)}
                                className="w-20"
                                data-testid="ai-split-count-input"
                            />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            AI reads the whole PDF and picks natural topic breaks across
                            the chapter, in batches of 5.
                        </p>
                    </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t border-border">
                    <Button variant="ghost" onClick={close} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !file}
                        className="gap-1.5 bg-primary hover:bg-primary/90"
                        data-testid="ai-split-submit"
                    >
                        {busy
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Sparkles className="h-4 w-4" />}
                        Start AI split
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
